/**
 * Conversion d'une saisie numerique en nombre.
 *
 * OBLIGATOIRE sur tout champ numerique depuis la migration au kit. `InputComponent`
 * expose `value = model<string>('')` et notifie `onChange(value: string)` : il pousse
 * donc une CHAINE dans le `FormControl`, quel que soit son `type`. Les ecrans
 * declarent pourtant des `FormControl<number>` et des payloads en `number`.
 *
 * Avant le kit, `<input matInput type="number">` passait par le `NumberValueAccessor`
 * d'Angular et produisait un vrai nombre — la conversion etait implicite. Elle ne
 * l'est plus, et le filtre de saisie (`/[0-9.,]/`) autorise la virgule decimale :
 * une saisie `1,50` part telle quelle dans le JSON, la ou le backend attend `1.5`.
 *
 * Les validateurs d'Angular ne rattrapent pas le probleme : `Validators.min` fait un
 * `parseFloat`, qui s'arrete a la virgule et lit `1,50` comme `1`.
 *
 * @see CustomValidators.minNombre / maxNombre pour le versant validation
 */

/**
 * `null` plutot que `NaN` ou `0` pour une saisie vide ou invalide : `NaN` se propage
 * silencieusement dans un payload, et `0` est une valeur metier legitime qu'on ne peut
 * pas confondre avec « rien saisi ».
 */
export function nombreDepuisSaisie(valeur: unknown): number | null {
  if (typeof valeur === 'number') {
    return Number.isFinite(valeur) ? valeur : null;
  }

  if (typeof valeur !== 'string') {
    return null;
  }

  /* `Number` et non `parseFloat` : `parseFloat('12abc')` rend `12`, ce qui ferait
     passer une saisie invalide pour un montant valide. `Number` rend `NaN`, donc
     `null` ici. Seule la PREMIERE virgule est convertie : `1,5,5` reste invalide. */
  const texte = valeur.trim().replace(',', '.');
  if (texte === '') {
    return null;
  }

  const nombre = Number(texte);
  return Number.isFinite(nombre) ? nombre : null;
}

/**
 * Variante pour les payloads dont le champ est requis : rend `repli` (0 par defaut)
 * plutot que `null`, en un seul endroit au lieu d'un `?? 0` a chaque appel.
 */
export function nombreDepuisSaisieOuRepli(valeur: unknown, repli = 0): number {
  return nombreDepuisSaisie(valeur) ?? repli;
}

/**
 * Le chemin RETOUR : ce qu'on reecrit DANS le champ quand l'utilisateur en sort.
 *
 * Le kit ne renormalise rien : `010` saisi reste `010` affiche, alors que la
 * soumission enverra `10` et que le libelle recapitulatif dira `10 %`. Trois
 * ecritures de la meme donnee sur le meme ecran.
 *
 * Rend la chaine vide sur une saisie vide ou invalide, et NON un tiret : dans un
 * champ editable, l'absence signifie « rien saisi », pas « valeur inconnue ». C'est
 * la difference avec `MONTANT_ABSENT`, qui parle d'affichage.
 *
 * ⚠️ SANS decimalesMax, SANS PERTE — et ce n'est pas un detail : le resultat est
 * REECRIT dans le controle, donc c'est lui qui part au serveur. Une premiere version
 * plafonnait TOUJOURS a deux decimales via `Intl` — un taux de commission saisi a
 * `2,375` etait soumis a `2,38`. Le plafonnement ne se demande donc que la ou il est
 * une regle metier (`decimalesMax`), jamais par confort d'affichage.
 *
 * Sans `decimalesMax`, la representation la plus COURTE qui se relit a l'identique —
 * donc ni perte ni plafond arbitraire — mais JAMAIS en notation exponentielle (voir
 * {@link sansExponentielle}) : `String(1e-7)` rendrait `'1e-7'`, un texte que le
 * filtre de saisie (`allowedChars` sans `e`) ne laisse plus retaper.
 *
 * AVEC `decimalesMax`, le nombre de decimales est PADE, pas seulement borne :
 * `1,5` devient `1,50`. Un montant en euros s'ecrit avec ses deux decimales meme
 * quand la seconde est nulle. L'arrondi est DEMI-SUPERIEUR sur la saisie decimale
 * (voir {@link arrondiDecimal}) : `1,005` rend `1,01` comme le fera la colonne
 * `decimal` en base, la ou un `toFixed` direct rendrait `1,00`.
 *
 * @param decimalesMax nombre de decimales — bornees ET completees. Omis = aucune limite.
 */
export function saisieNormalisee(valeur: unknown, decimalesMax?: number): string {
  const nombre = nombreDepuisSaisie(valeur);
  if (nombre === null) {
    return '';
  }

  const texte = decimalesMax === undefined ? sansExponentielle(nombre) : arrondiDecimal(nombre, decimalesMax);
  return texte.replace('.', ',');
}

/**
 * La representation la plus courte du nombre, sans notation exponentielle PARTOUT
 * ou une ecriture decimale fidele existe.
 *
 * `String(0.0000001)` rend `'1e-7'` : reecrit dans le champ, ce texte n'est plus
 * ressaisissable — le filtre `allowedChars` (`/[0-9.,]/`) refuse le `e` — meme si
 * `nombreDepuisSaisie('1e-7')` le relit correctement (donnee juste, edition cassee).
 * `toLocaleString` ecrit les decimales en clair (`'0.0000001'`, verifie au banc),
 * sans rien changer aux nombres ordinaires (`2.375` reste `'2.375'`).
 *
 * ⚠️ La garde de FIDELITE n'est pas decorative : `maximumFractionDigits: 20`
 * PLAFONNE les decimales, donc sous `1e-20` `toLocaleString` rend `'0'` — qui
 * serait reecrit dans le controle puis SOUMIS. Un zero silencieux est pire que
 * l'exponentielle exacte qu'on cherchait a eviter (attrape en re-review, MR !29).
 * Quand le texte ne se relit pas a l'identique, on rend `String(nombre)` :
 * l'exposant redevient la SEULE ecriture fidele, et l'invariant « sans perte » de
 * `saisieNormalisee` prime sur le confort d'edition. Ne pas relever le plafond a
 * la place : 20 est le maximum d'ES2020, releve seulement en ES2023 — la garde,
 * elle, ne depend d'aucune version.
 *
 * `useGrouping: false` est OBLIGATOIRE : avec le groupement, `1234.5` deviendrait
 * illisible par `nombreDepuisSaisie` (`Number('1 000')` vaut `NaN`, verifie) — le
 * piege deja documente en tete de `saisieNormalisee`. `en-US` et non `fr-FR` : les
 * deux branches rendent le POINT decimal, converti en virgule par l'appelant.
 */
function sansExponentielle(nombre: number): string {
  const texte = nombre.toLocaleString('en-US', { maximumFractionDigits: 20, useGrouping: false });
  return Number(texte) === nombre ? texte : String(nombre);
}

/**
 * Arrondi DEMI-SUPERIEUR a `decimales` decimales, rendu avec ses zeros de queue.
 *
 * `toFixed` seul ne suffit pas : il arrondit le DOUBLE, pas la saisie decimale.
 * `1.005` est stocke `1.00499…`, donc `(1.005).toFixed(2)` rend `1.00` (verifie) —
 * alors que SQL Server, sur la meme saisie en `decimal`, arrondit a `1.01`. Comme le
 * resultat est reecrit dans le controle puis SOUMIS, l'ecart serait stocke. D'ou le
 * decalage sur la CHAINE decimale : `'1.005e2'` se parse en `100.5` EXACTEMENT
 * (100.5 est representable), avant tout arrondi binaire.
 *
 * `Math.round` arrondit le demi vers le HAUT, comme SQL sur un montant positif —
 * il divergerait sur un negatif (`-100.5` → `-100`), mais les champs concernes
 * refusent le signe (`allowedChars` sans `-`).
 */
function arrondiDecimal(nombre: number, decimales: number): string {
  const decale = Number(`${nombre}e${decimales}`);
  if (!Number.isFinite(decale)) {
    /* `String(nombre)` passe en notation exponentielle hors [1e-6, 1e21[ et le
       decalage textuel ne s'applique plus — sans objet pour un montant saisi. */
    return nombre.toFixed(decimales);
  }
  return (Math.round(decale) / 10 ** decimales).toFixed(decimales);
}
